import type React from 'react';
import type { QuickFillItem } from '../../../shared/types';
import type { ViewportRange } from './quickFillNavigationModel';
import type {
  QuickFillNavigationDockProps,
  QuickFillNavigationMarker,
} from '../ui/components/QuickFillNavigationDock';

export interface BuildQuickFillNavigationDockPropsInputs {
  quickFillData: QuickFillItem[];
  filteredRowCount: number;
  navigatorRowCoverage: number[];
  collapsed: boolean;
  setNavigationDockCollapsed: React.Dispatch<React.SetStateAction<boolean>>;
  collapseLocked: boolean;
  activeNavigationRowLabel: string | null;
  activeNavigationPosition: number | null;
  activeRowMarked: boolean;
  canJumpToActive: boolean;
  canToggleMarker: boolean;
  hiddenMarkerCount: number;
  markers: QuickFillNavigationMarker[];
  viewportRange?: ViewportRange;
  handlers: {
    handleJumpToPosition: (position: number) => void;
    handleToggleActiveMarker: () => void;
    handleRemoveMarker: (rowIndex: number) => void;
    handleJumpToActiveLocation: () => void;
  };
  refs: {
    navigationDockRef: React.RefObject<HTMLDivElement | null>;
  };
  visibility: {
    isValidationScopedView: boolean;
    propertyPanelOpen: boolean;
    shouldShowValuePanel: boolean;
    useInlineTourDrawers: boolean;
  };
}

/**
 * Pure builder for the QuickFillNavigationDock props.
 *
 * Assembles the navigator state, markers, coverage and handlers into the
 * shape expected by the dock. Returns null when the dock should not render
 * (empty dataset, inline tour drawers, or any of the visibility conditions
 * that hide the dock in the QuickFill workspace).
 */
export function buildQuickFillNavigationDockProps(
  inputs: BuildQuickFillNavigationDockPropsInputs
): QuickFillNavigationDockProps | null {
  const {
    quickFillData,
    filteredRowCount,
    navigatorRowCoverage,
    collapsed,
    setNavigationDockCollapsed,
    collapseLocked,
    activeNavigationRowLabel,
    activeNavigationPosition,
    activeRowMarked,
    canJumpToActive,
    canToggleMarker,
    hiddenMarkerCount,
    markers,
    viewportRange,
    handlers,
    visibility,
  } = inputs;

  if (
    quickFillData.length === 0 ||
    filteredRowCount === 0 ||
    visibility.isValidationScopedView ||
    visibility.propertyPanelOpen ||
    visibility.shouldShowValuePanel ||
    visibility.useInlineTourDrawers
  ) {
    return null;
  }

  return {
    collapsed,
    collapseLocked,
    totalRows: quickFillData.length,
    filteredRowCount,
    rowCoverage: navigatorRowCoverage,
    currentPosition: activeNavigationPosition,
    viewportStart: viewportRange?.startPosition ?? null,
    viewportEnd: viewportRange?.endPosition ?? null,
    activeRowLabel: activeNavigationRowLabel,
    markers,
    hiddenMarkerCount,
    activeRowMarked,
    canJumpToActive,
    canToggleMarker,
    onToggleCollapsed: () => setNavigationDockCollapsed((previous) => !previous),
    onJumpToPosition: handlers.handleJumpToPosition,
    onJumpToActive: handlers.handleJumpToActiveLocation,
    onToggleMarkerForActive: handlers.handleToggleActiveMarker,
    onRemoveMarker: handlers.handleRemoveMarker,
  };
}
